import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import FormField from '../ui/FormField';
import SearchableSelect from '../SearchableSelect';

interface ActivityOption {
  _id: number;
  name: string;
}

interface IActivityPickerProps {
  /** Currently selected activity id (null = unassigned / org-wide only). */
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * Whether the picker is required. Scoped admins typically require an
   * activity selection; org-wide admins may leave it unassigned.
   */
  required?: boolean;
  /**
   * Narrow the picker to a set of activity ids (for scoped admins).
   * Undefined = no narrowing (org-wide admin sees all activities).
   * Empty array = no activities available.
   */
  allowedActivityIds?: number[];
  /** Optional label override (defaults to t('activity.picker.label')). */
  label?: string;
  /** Optional help text below the select. */
  helpText?: string;
  /** Disable the select (e.g., editing a template owned by another activity). */
  disabled?: boolean;
  /** Error message to display under the field. */
  error?: string;
}

/**
 * ActivityPicker — reusable activity dropdown for Subject Separation.
 *
 * Behaviour:
 * - For scoped-single admins (allowedActivityIds.length === 1) the picker is
 *   pre-filled and disabled.
 * - For scoped-multi admins the picker shows only their allowed activities.
 * - For org-wide admins the picker shows all activities plus an
 *   "Unassigned (org-wide only)" option.
 */
const ActivityPicker: React.FC<IActivityPickerProps> = ({
  value,
  onChange,
  required = false,
  allowedActivityIds,
  label,
  helpText,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation();
  const [allActivities, setAllActivities] = useState<ActivityOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.activities.list();
        if (!cancelled) {
          setAllActivities((res.data as ActivityOption[]).map(a => ({
            _id: Number(a._id),
            name: a.name,
          })));
        }
      } catch {
        if (!cancelled) setAllActivities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Narrow to allowed activities if caller provided a whitelist.
  const filteredActivities = useMemo(() => {
    if (!allowedActivityIds) return allActivities;
    const allowed = new Set(allowedActivityIds);
    return allActivities.filter(a => allowed.has(a._id));
  }, [allActivities, allowedActivityIds]);

  const isScopedSingle = allowedActivityIds?.length === 1;
  const effectiveDisabled = disabled || isScopedSingle;

  // Auto-fill for scoped-single admins.
  useEffect(() => {
    if (isScopedSingle && value !== allowedActivityIds![0]) {
      onChange(allowedActivityIds![0]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScopedSingle, allowedActivityIds?.[0]]);

  if (loading) {
    return <p className="form-help-text">{t('common.loading')}</p>;
  }

  return (
    <FormField
      htmlFor="activity-picker"
      label={label || t('activity.picker.label')}
      error={error}
      required={required}
    >
      <SearchableSelect
        id="activity-picker"
        options={filteredActivities}
        value={value}
        onChange={(v) => onChange(v == null ? null : Number(v))}
        disabled={effectiveDisabled}
        allowClear={!required && !allowedActivityIds}
        placeholder={
          !required && !allowedActivityIds
            ? t('activity.picker.unassigned')
            : t('activity.picker.selectPlaceholder')
        }
      />
      {helpText && <small className="form-help-text">{helpText}</small>}
      {isScopedSingle && (
        <small className="form-help-text">{t('activity.picker.scopedSingleHint')}</small>
      )}
    </FormField>
  );
};

export default ActivityPicker;
